import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';import { CalendarOff, Plus, Trash2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';

const emptyRange = () => ({
  date_from: '', date_to: '', all_day: true, start_time: '09:00', end_time: '13:00', reason: '',
});

export const periodLabel = (item) => {
  const from = item.date_from ? format(parseISO(item.date_from), 'd MMM yyyy', { locale: it }) : '';
  const to = item.date_to && item.date_to !== item.date_from
    ? ` – ${format(parseISO(item.date_to), 'd MMM yyyy', { locale: it })}` : '';
  const time = item.all_day ? '' : ` · ${item.start_time}–${item.end_time}`;
  return `${from}${to}${time}`;
};

/** Form condiviso da chiusure del salone e assenze degli operatori. */
export const RangeForm = ({ onSave, testPrefix }) => {
  const [form, setForm] = useState(emptyRange());
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.date_from) return toast.error('Indica la data di inizio');
    setBusy(true);
    const ok = await onSave({ ...form, date_to: form.date_to || form.date_from });
    setBusy(false);
    if (ok) setForm(emptyRange());
  };

  return (
    <div className="space-y-3 rounded-lg border border-dashed p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs">Dal</Label>
          <Input type="date" value={form.date_from} className="h-9"
            onChange={(e) => setForm({ ...form, date_from: e.target.value })}
            data-testid={`${testPrefix}-from`} />
        </div>
        <div>
          <Label className="text-xs">Al (lascia vuoto per un giorno solo)</Label>
          <Input type="date" value={form.date_to} className="h-9"
            onChange={(e) => setForm({ ...form, date_to: e.target.value })}
            data-testid={`${testPrefix}-to`} />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={form.all_day} onCheckedChange={(v) => setForm({ ...form, all_day: v })}
          data-testid={`${testPrefix}-allday`} />
        <span className="text-sm">Giornata intera</span>
      </div>
      {!form.all_day && (
        <div className="flex items-center gap-2">
          <Input type="time" value={form.start_time} className="h-9 w-[7.5rem]"
            onChange={(e) => setForm({ ...form, start_time: e.target.value })}
            data-testid={`${testPrefix}-start`} />
          <span className="text-muted-foreground">–</span>
          <Input type="time" value={form.end_time} className="h-9 w-[7.5rem]"
            onChange={(e) => setForm({ ...form, end_time: e.target.value })}
            data-testid={`${testPrefix}-end`} />
        </div>
      )}
      <Input placeholder="Motivo (facoltativo): ferie, formazione, festività..." value={form.reason}
        className="h-9" onChange={(e) => setForm({ ...form, reason: e.target.value })}
        data-testid={`${testPrefix}-reason`} />
      <Button size="sm" onClick={submit} disabled={busy} data-testid={`${testPrefix}-save`}>
        <Plus className="mr-1.5 h-3.5 w-3.5" />
        Aggiungi
      </Button>
    </div>
  );
};

/** Festività italiane: un interruttore per chiudere il salone in quel giorno. */
const HolidaysList = () => {
  const { API, getAuthHeaders } = useAuth();
  const [holidays, setHolidays] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/holidays`, getAuthHeaders());
      setHolidays(res.data);
    } catch {
      setHolidays([]);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (holiday, value) => {
    setHolidays(prev => prev.map(h => (h.date === holiday.date ? { ...h, enabled: value } : h)));
    try {
      if (value) {
        await axios.post(`${API}/holidays`, { date: holiday.date, name: holiday.name }, getAuthHeaders());
      } else {
        await axios.delete(`${API}/holidays/${holiday.date}`, getAuthHeaders());
      }
    } catch {
      toast.error('Modifica non riuscita');
      load();
    }
  };

  return (
    <div className="divide-y rounded-lg border" data-testid="holidays-list">
      {holidays.map((h) => (
        <div key={h.date} className="flex items-center gap-3 px-3 py-2.5" data-testid={`holiday-${h.date}`}>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{h.name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {format(parseISO(h.date), 'EEEE d MMMM yyyy', { locale: it })}
              {h.already_closed && <span> · di norma già chiuso</span>}
            </p>
          </div>
          <Switch
            checked={h.enabled}
            onCheckedChange={(v) => toggle(h, v)}
            data-testid={`holiday-toggle-${h.date}`}
          />
        </div>
      ))}
      {holidays.length === 0 && (
        <p className="px-3 py-4 text-sm text-muted-foreground">Nessuna festività nei prossimi 12 mesi.</p>
      )}
    </div>
  );
};

/** Chiusure straordinarie del salone: ferie, festività, mezze giornate. */
export const ClosuresCard = () => {
  const { API, getAuthHeaders } = useAuth();
  const [closures, setClosures] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/closures`, getAuthHeaders());
      setClosures(res.data);
    } catch {
      setClosures([]);
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const add = async (payload) => {
    try {
      await axios.post(`${API}/closures`, payload, getAuthHeaders());
      toast.success('Chiusura aggiunta');
      load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Non salvata');
      return false;
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/closures/${id}`, getAuthHeaders());
      load();
    } catch {
      toast.error('Eliminazione non riuscita');
    }
  };

  return (
    <Card data-testid="closures-card">
      <CardHeader>
        <CardTitle className="font-outfit flex items-center gap-2">
          <CalendarOff className="w-5 h-5 text-primary" />
          Chiusure straordinarie
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Ferie, festività o mezze giornate: in quei periodi l’agenda non accetta nuovi appuntamenti.
        </p>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="custom">
          <TabsList className="mb-4" data-testid="closures-tabs">
            <TabsTrigger value="custom" data-testid="closures-tab-custom">Le tue chiusure</TabsTrigger>
            <TabsTrigger value="holidays" data-testid="closures-tab-holidays">Festività</TabsTrigger>
          </TabsList>

          <TabsContent value="custom" className="space-y-4">
            {closures.filter(c => !c.holiday).length > 0 ? (
              <div className="space-y-2">
                {closures.filter(c => !c.holiday).map((c) => (
                  <div key={c.id} className="flex items-center gap-3 rounded-lg border px-3 py-2" data-testid={`closure-${c.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{periodLabel(c)}</p>
                      {c.reason && <p className="truncate text-xs text-muted-foreground">{c.reason}</p>}
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(c.id)}
                      data-testid={`closure-delete-${c.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="closures-empty">
                Nessuna chiusura programmata.
              </p>
            )}
            <RangeForm onSave={add} testPrefix="closure-new" />
          </TabsContent>

          <TabsContent value="holidays">
            <HolidaysList />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
